import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import avatarImage from './image.png';
import logoImg from './assets/logo.jpg';
import bgLeftImg from './assets/bg-left.webp';
import bgRightImg from './assets/bg-right.webp';
import bgCenterImg from './assets/bg-center.webp';
import { navigate, usePathname } from './hooks/usePathname';
import { useAudioMute } from './hooks/useAudioMute';

import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminIPs from './pages/AdminIPs';
import AdminChats from './pages/AdminChats';
import usePretextRepel from './components/PretextLayoutViz';
import { formatFastApiDetail } from './utils/fastApiError';

const ADMIN_UNLOCK_PHRASE = '成剑芝麻开门';
const COOPERATION_PHRASE = '项目合作芝麻开门';
const COOPERATION_PATH = '/cooperation.html';

async function readHttpErrorMessage(response) {
  const ct = response.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    const j = await response.json().catch(() => ({}));
    const d = j.detail;
    if (d && typeof d === 'object' && !Array.isArray(d) && d.message) return String(d.message);
    return formatFastApiDetail(d) || `请求失败（HTTP ${response.status}）`;
  }
  const t = await response.text().catch(() => '');
  return t.trim().slice(0, 280) || `请求失败（HTTP ${response.status}）`;
}

function getSessionId() {
  let sid = sessionStorage.getItem('_cj_sid');
  if (!sid) {
    sid = Date.now().toString(36) + Math.random().toString(36).slice(2);
    sessionStorage.setItem('_cj_sid', sid);
  }
  return sid;
}

function stripMarkdown(text) {
  if (!text) return text;
  return text
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/(?<!\w)\*(?!\s)(.+?)(?<!\s)\*(?!\w)/g, '$1')
    .replace(/(?<!\w)_(?!\s)(.+?)(?<!\s)_(?!\w)/g, '$1')
    .replace(/^>\s+/gm, '')
    .replace(/^[-*]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^---+$/gm, '')
    .replace(/^\*\*\*+$/gm, '');
}
const ADMIN_UNLOCK_STORAGE_KEY = 'cj_admin_unlocked';

function Link({ to, active, children }) {
  return (
    <span
      className={`navLink ${active ? 'navLinkActive' : ''}`}
      onClick={() => navigate(to)}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && navigate(to)}
    >
      {children}
    </span>
  );
}

function TopNav({ active, onLogout, adminUnlocked, onPlayIntro, showIntroBtn }) {
  const audio = useAudioMute();

  return (
    <div className="topNav">
      <div className="topNavInner">
        <div className="navLeft">
          <div className="audioControl">
            <button
              className="iconBtn"
              aria-label={audio.muted ? '开启音乐' : '关闭音乐'}
              data-tooltip={audio.muted ? '开启音乐' : '关闭音乐'}
              onClick={audio.toggle}
            >
              <img className="iconBtnImg" alt="logo" src={logoImg} />
            </button>
            <span className="audioControlLabelWrap">
              <span className="audioControlLabel">背景音乐</span>
              <span className="hourglass" aria-hidden="true">
                <svg className="hourglassSvg" viewBox="0 0 64 64" focusable="false">
                  <defs>
                    <linearGradient id="hgStroke" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#007AFF" stopOpacity="0.95" />
                      <stop offset="50%" stopColor="#6E40C9" stopOpacity="0.95" />
                      <stop offset="100%" stopColor="#2EE59D" stopOpacity="0.95" />
                    </linearGradient>
                    <linearGradient id="hgSandTopGrad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#6E40C9" />
                      <stop offset="50%" stopColor="#007AFF" />
                      <stop offset="100%" stopColor="#FFD166" />
                    </linearGradient>
                    <linearGradient id="hgSandBottomGrad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#2EE59D" />
                      <stop offset="50%" stopColor="#00C6FF" />
                      <stop offset="100%" stopColor="#6E40C9" />
                    </linearGradient>
                    <linearGradient id="hgStreamGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                      <stop offset="50%" stopColor="rgba(255,255,255,0.8)" />
                      <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                    </linearGradient>
                    <clipPath id="hgClip">
                      <path d="M24 12h16v6c0 7.2-4.4 12.8-8 14.6 3.6 1.8 8 7.4 8 14.6v6H24v-6c0-7.2 4.4-12.8 8-14.6-3.6-1.8-8-7.4-8-14.6v-6z" />
                    </clipPath>
                  </defs>

                  {/* outer glass */}
                  <path
                    className="hgFramePath"
                    d="M20 8h24v8c0 8.4-5.2 15.6-12 18.2 6.8 2.6 12 9.8 12 18.2v8H20v-8c0-8.4 5.2-15.6 12-18.2-6.8-2.6-12-9.8-12-18.2V8z"
                    fill="rgba(255,255,255,0.06)"
                    stroke="url(#hgStroke)"
                    strokeWidth="1.2"
                  />
                  <path
                    className="hgInnerGlass"
                    d="M24 12h16v6c0 7.2-4.4 12.8-8 14.6 3.6 1.8 8 7.4 8 14.6v6H24v-6c0-7.2 4.4-12.8 8-14.6-3.6-1.8-8-7.4-8-14.6v-6z"
                    fill="rgba(255,255,255,0.06)"
                    stroke="rgba(255,255,255,0.18)"
                    strokeWidth="0.9"
                  />

                  {/* sand (clipped inside glass) */}
                  <g clipPath="url(#hgClip)">
                    <path className="hgSandTop" d="M24 16h16l-8 14z" fill="url(#hgSandTopGrad)" />
                    <path className="hgSandBottom" d="M32 48L24 62h16z" fill="url(#hgSandBottomGrad)" />
                    <rect className="hgStream" x="31" y="30" width="2" height="18" rx="1" fill="url(#hgStreamGrad)" />
                    <circle className="hgParticle hgParticle1" cx="32" cy="31" r="1.2" fill="rgba(255,255,255,0.7)" />
                    <circle className="hgParticle hgParticle2" cx="32" cy="31" r="1.0" fill="rgba(255,255,255,0.6)" />
                    <circle className="hgParticle hgParticle3" cx="32" cy="31" r="0.9" fill="rgba(255,255,255,0.55)" />
                  </g>

                  {/* specular highlight */}
                  <path
                    className="hgHighlight"
                    d="M26 14c5-4 11-4 16 0"
                    fill="none"
                    stroke="rgba(255,255,255,0.30)"
                    strokeWidth="1"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </span>
          </div>
          {adminUnlocked ? (
            <div className="navLinks">
              <Link to="/administer/dashboard" active={active === 'dashboard'}>Dashboard</Link>
              <Link to="/administer/ips" active={active === 'ips'}>IPs</Link>
              <Link to="/administer/chats" active={active === 'chats'}>Chats</Link>
            </div>
          ) : null}
        </div>
        <div className="navRight">
          {showIntroBtn && onPlayIntro ? (
            <button className="pillBtn pillBtnGhost" onClick={onPlayIntro}>
              自我介绍视频
            </button>
          ) : null}
          {adminUnlocked && onLogout ? (
            <button className="pillBtn" onClick={onLogout}>Logout</button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const MessageBubble = React.memo(function MessageBubble({ msg, onSendSuggestion, loading, conversationRounds }) {
  const botBubbleRef = useRef(null);
  const botPlain = msg.type === 'bot' ? stripMarkdown(msg.content) : '';
  usePretextRepel(botBubbleRef, botPlain);

  return (
    <div className={`msgRow ${msg.type === 'user' ? 'msgRowUser' : ''}`}>
      <div className="msgBubbleWrap">
        {msg.type === 'bot' && msg.metadata && (
          <div className="msgMetadata">
            {msg.metadata.intent && msg.metadata.intent !== 'greeting' && (
              <span className={`metaTag metaIntent metaIntent-${msg.metadata.intent}`}>
                {msg.metadata.intent === 'technical_discussion' && '技术探讨'}
                {msg.metadata.intent === 'resume_query' && '简历查询'}
                {msg.metadata.intent === 'open_topic' && '开放话题'}
              </span>
            )}
            {msg.metadata.technicalDepth && msg.metadata.technicalDepth !== 'intro' && (
              <span className={`metaTag metaDepth metaDepth-${msg.metadata.technicalDepth}`}>
                {msg.metadata.technicalDepth === 'basic' && '基础'}
                {msg.metadata.technicalDepth === 'intermediate' && '中级'}
                {msg.metadata.technicalDepth === 'advanced' && '高级'}
              </span>
            )}
          </div>
        )}
        
        <div
          ref={msg.type === 'bot' ? botBubbleRef : undefined}
          className={`msgBubble ${msg.type === 'user' ? 'msgUser' : msg.type === 'error' ? 'msgError' : 'msgBot'}`}
        >
          {msg.type === 'bot' ? botPlain : msg.content}
        </div>
        
        {msg.type === 'bot' && 
         msg.metadata?.keyConcepts && 
         msg.metadata.keyConcepts.length > 0 && 
         msg.metadata.keyConcepts.some(c => c && c.trim()) && (
          <div className="msgConcepts">
            {msg.metadata.keyConcepts
              .filter(c => c && c.trim())
              .slice(0, 8)
              .map((concept, idx) => (
                <span key={idx} className="conceptTag">{concept}</span>
              ))
            }
          </div>
        )}
        
        {msg.type === 'bot' && msg.followUpSuggestions && msg.followUpSuggestions.length > 0 && (
          <div className="msgFollowUps">
            <div className="followUpLabel">您可能还想了解：</div>
            {msg.followUpSuggestions.map((suggestion, idx) => (
              <button 
                key={idx} 
                className="followUpBtn"
                onClick={() => onSendSuggestion(suggestion)}
                disabled={loading}
              >
                {suggestion}
              </button>
            ))}
            {conversationRounds >= 3 && (
              <button 
                className="followUpBtn followUpBtnContact"
                onClick={() => onSendSuggestion("如何联系您？我想进一步沟通")}
                disabled={loading}
              >
                如何联系您？
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

const MessageList = React.memo(function MessageList({ messages, loading, conversationRounds, onSendSuggestion }) {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  return (
    <div className="chatBody">
      {messages.map((msg) => (
        <MessageBubble
          key={msg.id}
          msg={msg}
          onSendSuggestion={onSendSuggestion}
          loading={loading}
          conversationRounds={conversationRounds}
        />
      ))}
      <div ref={bottomRef} />
    </div>
  );
});

function ChatInputBar({ onSend, loading, isRateLimited }) {
  const [input, setInput] = useState('');

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || loading) return;
    onSend(text);
    setInput('');
  }, [input, loading, onSend]);

  return (
    <div className="chatInputBar">
      <textarea
        className="textArea"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder={isRateLimited ? '今日提问次数已用完，请明天再来…' : '如果您有技术问题想聊，或者想了解我的项目经历、联系方式，都可以问。点击背景音乐左边的C可以开关背景音乐哦~'}
        disabled={loading || isRateLimited}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
          }
        }}
      />
      <button className="primaryBtn" onClick={handleSend} disabled={!input.trim() || loading || isRateLimited}>
        {isRateLimited ? '已达限制' : '发送'}
      </button>
    </div>
  );
}

// 视频流接口：后端按 HTTP Range 分片返回，浏览器边下边播
const INTRO_VIDEO_SRC = '/api/media/intro';
const INTRO_VIDEO_POSTER = '/api/media/intro/poster';

function VideoModal({ open, onClose, autoplayMuted = false }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(autoplayMuted);

  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (open) {
      // 进入弹窗：从头开始；首次自动播放要静音才能绕过浏览器策略
      v.muted = autoplayMuted;
      setMuted(autoplayMuted);
      v.currentTime = 0;
      const p = v.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          // 极端情况下仍被拦截：保持静音状态再试一次
          v.muted = true;
          setMuted(true);
          v.play().catch(() => {});
        });
      }
    } else {
      v.pause();
    }
  }, [open, autoplayMuted]);

  const handleUnmute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    setMuted(false);
    // 用户手势触发，必定可以发声播放
    v.play().catch(() => {});
  };

  if (!open) return null;

  return (
    <div className="videoOverlay" onClick={onClose}>
      <div className="videoModal" onClick={(e) => e.stopPropagation()}>
        <button className="videoCloseBtn" onClick={onClose} aria-label="关闭">&times;</button>
        <video
          ref={videoRef}
          className="videoPlayer"
          src={INTRO_VIDEO_SRC}
          poster={INTRO_VIDEO_POSTER}
          controls
          playsInline
          preload="metadata"
          controlsList="nodownload noremoteplayback noplaybackrate"
          disablePictureInPicture
          onContextMenu={(e) => e.preventDefault()}
        />
        {muted ? (
          <button className="videoUnmuteBtn" onClick={handleUnmute} aria-label="开启声音">
            点击开启声音
          </button>
        ) : null}
      </div>
    </div>
  );
}

function ChatHome({ onUnlockAdmin, onOpenIntroVideo }) {
  const [messages, setMessages] = useState([
    { 
      id: 1, 
      type: 'bot', 
      content: '您好，我是成剑。\n\n我在 AI 工程和系统架构方面有一些实践经验，主要是 AI全栈开发 和 大模型应用 这两个方向。\n\n如果您有技术问题想聊，或者想了解我的项目经历，都可以问。\n\n您也可以点击上方头像观看AI动漫自我介绍。',
      metadata: {
        intent: 'greeting',
        keyConcepts: ['AI全栈开发', '大模型应用'],
        technicalDepth: 'intro'
      }
    }
  ]);
  const [loading, setLoading] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const chatHistoryRef = useRef([]);
  const [conversationRounds, setConversationRounds] = useState(0);

  const botContentRef = useRef('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch('/api/chat/rate-limit');
        if (response.ok && !cancelled) setRateLimitInfo(await response.json());
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSend = useCallback(async (text) => {
    if (!text.trim() || loading) return;
    
    if (text.trim() === ADMIN_UNLOCK_PHRASE) {
      onUnlockAdmin?.();
      return;
    }

    if (text.trim() === COOPERATION_PHRASE) {
      window.location.href = COOPERATION_PATH;
      return;
    }
    
    const userMessage = { id: Date.now(), type: 'user', content: text };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);

    const recentHistory = chatHistoryRef.current.slice(-6);

    try {
      const response = await fetch('/api/chat/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          question: text,
          use_langgraph: true,
          chat_history: recentHistory
        })
      });

      if (!response.ok) {
        if (response.status === 429) {
          let msg = '请求次数已达上限，请明天再来提问。';
          try {
            const errorData = await response.json();
            msg = errorData?.detail?.message || msg;
          } catch { /* ignore */ }
          setMessages((prev) => [...prev, { id: Date.now() + 1, type: 'error', content: msg }]);
          setRateLimitInfo((prev) => ({ ...(prev || {}), remaining_requests: 0, max_requests: prev?.max_requests || 8 }));
        } else {
          const msg = await readHttpErrorMessage(response);
          setMessages((prev) => [...prev, { id: Date.now() + 1, type: 'error', content: msg }]);
        }
        setLoading(false);
        return;
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('event-stream')) {
        const preview = await response.text().catch(() => '');
        setMessages((prev) => [...prev, {
          id: Date.now() + 1,
          type: 'error',
          content: `对话接口未返回流式数据（Content-Type: ${contentType || '无'}）。请确认 nginx 是否把 /api/ 反代到后端，且未把 POST /api/chat/stream 指到静态首页。\n${preview.slice(0, 200)}`,
        }]);
        setLoading(false);
        return;
      }

      if (!response.body) {
        setMessages((prev) => [...prev, { id: Date.now() + 1, type: 'error', content: '浏览器未收到可读的数据流，请检查网络或 HTTPS 证书。' }]);
        setLoading(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      const botId = Date.now() + 1;
      botContentRef.current = '';
      
      setMessages((prev) => [...prev, { id: botId, type: 'bot', content: '', metadata: {} }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'rate_limit') {
                setRateLimitInfo({ 
                  remaining_requests: data.remaining_requests, 
                  max_requests: data.max_requests 
                });
              } else if (data.type === 'metadata') {
                setMessages((prev) => prev.map(msg => 
                  msg.id === botId
                    ? { 
                        ...msg, 
                        metadata: {
                          intent: data.intent,
                          keyConcepts: data.key_concepts || [],
                          technicalDepth: data.technical_depth,
                          sources: data.sources
                        }
                      }
                    : msg
                ));
              } else if (data.type === 'content') {
                botContentRef.current += data.content;
                const snapshot = botContentRef.current;
                setMessages((prev) => prev.map(msg =>
                  msg.id === botId ? { ...msg, content: snapshot } : msg
                ));
              } else if (data.type === 'follow_up') {
                setMessages((prev) => prev.map(msg => 
                  msg.id === botId
                    ? { ...msg, followUpSuggestions: data.suggestions }
                    : msg
                ));
              } else if (data.type === 'error') {
                const errText = data.error || '生成回答时出错';
                botContentRef.current = errText;
                setMessages((prev) => prev.map((msg) =>
                  msg.id === botId ? { ...msg, content: errText, metadata: { ...(msg.metadata || {}), streamError: true } } : msg
                ));
              }
            } catch (e) {
              console.error('Failed to parse SSE data:', e);
            }
          }
        }
      }

      if (!botContentRef.current.trim()) {
        setMessages((prev) => prev.map((msg) =>
          msg.id === botId && !msg.content?.trim()
            ? { ...msg, content: '未收到模型回复。请在后端日志中查看 OpenRouter / SiliconFlow 密钥与网络；本地可打开 /docs 测 POST /api/chat/stream。' }
            : msg
        ));
      }

      chatHistoryRef.current = [
        ...chatHistoryRef.current,
        { role: 'user', content: text },
        { role: 'assistant', content: botContentRef.current }
      ];

      setConversationRounds((prev) => prev + 1);

    } catch (error) {
      console.error('Chat error:', error);
      const hint = error && typeof error.message === 'string' ? error.message : '';
      setMessages((prev) => [...prev, {
        id: Date.now() + 1,
        type: 'error',
        content: hint
          ? `抱歉，对话请求失败：${hint}`
          : '抱歉，我暂时无法回答您的问题。请稍后再试。（多为网络中断、HTTPS 证书或接口不可达）',
      }]);
    } finally {
      setLoading(false);
    }
  }, [loading, onUnlockAdmin]);

  const isRateLimited = rateLimitInfo && rateLimitInfo.remaining_requests === 0;

  return (
    <div className="glass chatWrap">
      <div className="chatHeader">
        <div className="chatTitle">
          <button className="avatarWrap" onClick={onOpenIntroVideo} aria-label="播放视频介绍">
            <img className="avatar" src={avatarImage} alt="成剑头像" />
            <span className="avatarPlay">&#9654;</span>
          </button>
          <div>
            <div className="h1 chatName" style={{ margin: 0, fontSize: 20 }}>成剑</div>
            <div className="chatSubtitle">探索我的技术世界 · 了解我的项目经验 · 感受代码的魅力</div>
          </div>
        </div>
        <div className="small">
          {rateLimitInfo ? `今日剩余: ${rateLimitInfo.remaining_requests}/${rateLimitInfo.max_requests}` : ''}
        </div>
      </div>

      <MessageList
        messages={messages}
        loading={loading}
        conversationRounds={conversationRounds}
        onSendSuggestion={handleSend}
      />

      <ChatInputBar
        onSend={handleSend}
        loading={loading}
        isRateLimited={isRateLimited}
      />
    </div>
  );
}

async function adminMe() {
  const res = await fetch('/api/admin/me', { credentials: 'include' });
  return res.ok;
}

const INTRO_VIDEO_AUTOPLAYED_KEY = '_cj_intro_autoplayed';

export default function App() {
  const pathname = usePathname();
  const isAdmin = pathname.startsWith('/administer');

  const [authed, setAuthed] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [adminUnlocked, setAdminUnlocked] = useState(() => {
    try {
      return window.localStorage.getItem(ADMIN_UNLOCK_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  // 欢迎视频：首次进入主页自动播放（每次会话只触发一次），关闭后顶栏出现"自我介绍视频"按钮
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoAutoplay, setVideoAutoplay] = useState(false);

  useEffect(() => {
    if (isAdmin) return;
    let played = false;
    try {
      played = sessionStorage.getItem(INTRO_VIDEO_AUTOPLAYED_KEY) === '1';
    } catch { /* ignore */ }
    if (played) return;
    // 延迟一帧打开，避免阻塞首屏渲染
    const t = setTimeout(() => {
      setVideoAutoplay(true);
      setVideoOpen(true);
      try { sessionStorage.setItem(INTRO_VIDEO_AUTOPLAYED_KEY, '1'); } catch { /* ignore */ }
    }, 600);
    return () => clearTimeout(t);
  }, [isAdmin]);

  const openIntroVideo = useCallback(() => {
    setVideoAutoplay(false); // 用户手动点开，允许带声音播放
    setVideoOpen(true);
  }, []);

  const closeIntroVideo = useCallback(() => setVideoOpen(false), []);

  useEffect(() => {
    const sid = getSessionId();
    const ping = () => {
      fetch('/api/chat/track-heartbeat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sid })
      }).catch(() => {});
    };
    ping();
    const t = setInterval(ping, 30000);
    return () => clearInterval(t);
  }, []);

  const active = useMemo(() => {
    if (!isAdmin) return 'home';
    if (pathname.startsWith('/administer/ips')) return 'ips';
    if (pathname.startsWith('/administer/chats')) return 'chats';
    if (pathname.startsWith('/administer/dashboard')) return 'dashboard';
    return 'dashboard';
  }, [isAdmin, pathname]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!isAdmin) {
        setAuthChecked(false);
        return;
      }
      if (!adminUnlocked) {
        setAuthed(false);
        setAuthChecked(false);
        navigate('/');
        return;
      }
      const ok = await adminMe().catch(() => false);
      if (!mounted) return;
      setAuthed(ok);
      setAuthChecked(true);
      if (!ok && pathname !== '/administer') navigate('/administer');
      if (ok && pathname === '/administer') navigate('/administer/dashboard');
    })();
    return () => { mounted = false; };
  }, [isAdmin, pathname, adminUnlocked]);

  const logout = async () => {
    try { await fetch('/api/admin/logout', { method: 'POST', credentials: 'include' }); } catch { /* ignore */ }
    setAuthed(false);
    setAdminUnlocked(false);
    try { window.localStorage.removeItem(ADMIN_UNLOCK_STORAGE_KEY); } catch { /* ignore */ }
    navigate('/');
  };

  let content = null;
  if (!isAdmin) {
    content = (
      <ChatHome
        onUnlockAdmin={() => {
          setAdminUnlocked(true);
          try { window.localStorage.setItem(ADMIN_UNLOCK_STORAGE_KEY, '1'); } catch { /* ignore */ }
        }}
        onOpenIntroVideo={openIntroVideo}
      />
    );
  } else if (!adminUnlocked) {
    // 未解锁时不渲染任何后台内容，避免短暂闪烁；useEffect 会把路由带回首页
    content = null;
  } else if (!authChecked) {
    content = <div className="glass cardPad"><span className="tag">Loading…</span></div>;
  } else if (!authed) {
    content = <AdminLogin onLoggedIn={() => { setAuthed(true); navigate('/administer/dashboard'); }} />;
  } else {
    if (active === 'ips') content = <AdminIPs />;
    else if (active === 'chats') content = <AdminChats />;
    else content = <AdminDashboard />;
  }

  return (
    <div className="appRoot">
      <div className="bgDecor" aria-hidden="true">
        <img className="bgLeft" src={bgLeftImg} alt="" decoding="async" />
        <img className="bgRight" src={bgRightImg} alt="" decoding="async" />
        <img className="bgCenter" src={bgCenterImg} alt="" decoding="async" />
      </div>
      <TopNav
        active={active}
        onLogout={isAdmin && authed ? logout : null}
        adminUnlocked={adminUnlocked}
        onPlayIntro={openIntroVideo}
        showIntroBtn={!isAdmin}
      />
      <div className="container">
        {content}
      </div>
      <VideoModal open={videoOpen} onClose={closeIntroVideo} autoplayMuted={videoAutoplay} />
    </div>
  );
}